import {
  AudienceInstance,
  AudienceType,
  MatchingCriteria,
} from '@/client/services/api/graphql/gql/graphql';

import type { AssignmentFormData } from '../../types/assignmentTypes';
import { useBulkUserSearchFromEmails } from './useBulkUserSearchFromEmails';
import { useFormContext } from 'react-hook-form';
import { useToastStore } from '@/client/services/state/toastStore';
import { useTranslation } from 'react-i18next';

export const useBulkImportAudience = () => {
  const { t } = useTranslation();
  const { setToast } = useToastStore();
  const { bulkUserSearchFromEmailsMutation } = useBulkUserSearchFromEmails();

  const { setValue } = useFormContext<AssignmentFormData>();

  const handleFileUpload = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.txt,.json,.csv';

    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];

      if (file) {
        // * Handle the file upload here
        const reader = new FileReader();

        reader.onload = (event) => {
          const content = event.target?.result as string;
          const emails = new Set<string>();

          // Email regex pattern
          const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

          // Handle different file types
          if (file.name.endsWith('.json')) {
            try {
              const jsonData = JSON.parse(content);
              const emailList = Object.values(jsonData)[0];

              if (Array.isArray(emailList)) {
                emailList.forEach((email) => {
                  if (typeof email === 'string') {
                    if (emailPattern.test(email.trim())) {
                      emails.add(email.trim());
                    }
                  }
                });
              }
            } catch (err) {
              setToast({
                show: true,
                title: t('admin.assignments.step.audience.import.invalidJson'),
                status: 'error',
              });

              return;
            }
          } else {
            // Handle .txt and .csv files
            const lines = content.split(/[\r\n]+/);

            // For CSV files, look for email column
            if (file.name.endsWith('.csv')) {
              const headers = lines[0].split(',').map((h) => h.trim().toLowerCase());
              const emailColIndex = headers.indexOf('email');

              if (emailColIndex >= 0) {
                // Skip header row
                lines.slice(1).forEach((line) => {
                  const cols = line.split(',');
                  const email = cols[emailColIndex]?.trim();
                  if (email && emailPattern.test(email)) {
                    emails.add(email);
                  }
                });
              }
            } else {
              // Handle .txt files - assume one email per line
              lines.forEach((line) => {
                const email = line.trim();
                if (email && emailPattern.test(email)) {
                  emails.add(email);
                }
              });
            }
          }

          const validEmails = Array.from(emails);

          bulkUserSearchFromEmailsMutation
            .mutateAsync(validEmails)
            .then((users) => {
              if (users.length === 0) {
                setToast({
                  show: true,
                  title: t('admin.assignments.step.audience.import.user.noValid'),
                  status: 'error',
                });

                return;
              }

              setValue('bulkAudienceImportData', {
                usesBulkAudienceImport: true,
                bulkImportFileName: file.name,
              });

              setValue(
                'audience',
                users.map((user: any) => ({
                  id: user.id,
                  matchingCriteria: MatchingCriteria.MatchAny,
                  type: AudienceType.User,
                  name: user.fullName,
                  image: user.image,
                  segmentationType: 'user',
                })) as AudienceInstance[],
              );

              setToast({
                show: true,
                title: `${users.length} ${t('admin.assignments.step.audience.import.success')}`,
                status: 'success',
              });
            })
            .catch(() => {
              setToast({
                show: true,
                title: t('admin.assignments.step.audience.import.user.error'),
                status: 'error',
              });
            });
        };

        reader.readAsText(file);

        return;
      }

      setToast({
        show: true,
        title: t('admin.assignments.step.audience.import.error'),
        status: 'error',
      });
    };

    input.click();
  };

  return {
    handleFileUpload,
  };
};
